import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Clock, CheckCircle2, Hourglass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchMyDeals } from "@/lib/api";
import { TemperatureBadge } from "@/components/public/TemperatureBadge";

const STATUS = {
  pending: { label: "In Prüfung", cls: "bg-amber-50 text-amber-600", icon: Hourglass },
  posted: { label: "Veröffentlicht", cls: "bg-emerald-50 text-emerald-600", icon: CheckCircle2 },
  new: { label: "Veröffentlicht", cls: "bg-emerald-50 text-emerald-600", icon: CheckCircle2 },
  saved: { label: "Gespeichert", cls: "bg-slate-100 text-slate-600", icon: Clock },
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [deals, setDeals] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/profil");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) fetchMyDeals().then(setDeals).finally(() => setBusy(false));
  }, [user]);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="profile-page">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl ai-gradient-bg text-white flex items-center justify-center font-outfit font-black text-2xl">
          {user.name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="font-outfit font-black text-2xl tracking-tight text-slate-900">{user.name}</h1>
          <p className="text-sm text-slate-500 font-worksans">{user.email}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit font-bold text-xl text-slate-900">Meine Deals</h2>
        <Link to="/deal-einreichen" className="ai-gradient-bg text-white font-outfit font-bold rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5 active:scale-95 transition-all">
          <Plus className="h-4 w-4" /> Neuer Deal
        </Link>
      </div>

      {busy ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : deals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <img src="/wizard-mascot.png" alt="" className="h-24 mx-auto mb-3 opacity-80" />
          <p className="font-outfit font-bold text-slate-900">Noch keine Deals eingereicht</p>
          <p className="text-sm text-slate-500 font-worksans mt-1">Reiche deinen ersten Deal ein und werde Teil der Magie!</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-deals-list">
          {deals.map((d) => {
            const s = STATUS[d.status] || STATUS.pending;
            const Icon = s.icon;
            return (
              <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                <img src={d.image_url || "/wizard-icon.png"} alt="" className="h-14 w-14 rounded-lg object-cover bg-slate-50 border border-slate-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{d.product_name}</p>
                  <p className="text-sm text-slate-500 font-worksans">{d.deal_price.toFixed(2)} € · -{d.discount_percent}%</p>
                </div>
                {d.published && <TemperatureBadge temperature={d.temperature} />}
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
                  <Icon className="h-3.5 w-3.5" /> {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
